import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../../context/OrganizationContext';
import { getPresignedViewUrl } from '../../utils/fileUtils';

const DroneImageUpload = ({ currentImageUrl, onImageUpload, disabled }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { currentOrganization } = useOrganization();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Fetch presigned URL when currentImageUrl changes
  useEffect(() => {
    const fetchPresignedUrl = async () => {
      if (currentImageUrl && currentOrganization?.organizationId) {
        try {
          const presignedUrl = await getPresignedViewUrl(
            currentImageUrl,
            currentOrganization?.organizationUuid,
            getAccessTokenSilently
          );
          setPreviewUrl(presignedUrl || currentImageUrl);
        } catch (err) {
          setPreviewUrl(currentImageUrl); // Fallback to original URL
        }
      } else {
        setPreviewUrl(null);
      }
    };

    fetchPresignedUrl();
  }, [currentImageUrl, currentOrganization, getAccessTokenSilently]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      // Create preview
      const localPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(localPreviewUrl);

      const token = await getAccessTokenSilently();

      // Step 1: Request presigned URL from backend
      const urlResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/files/upload-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            category: 'drones'
          })
        }
      );

      if (!urlResponse.ok) {
        const errorData = await urlResponse.json();
        throw new Error(errorData.error || 'Failed to get upload URL');
      }

      const { presignedUrl, fileUrl } = await urlResponse.json();

      // Step 2: Upload file directly to S3 using presigned URL
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to storage');
      }

      // Step 3: Return the file URL to parent
      onImageUpload(fileUrl);

    } catch (err) {
      setError(err.message);
      setPreviewUrl(currentImageUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    onImageUpload(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>
        Drone Image
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {previewUrl ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            position: 'relative'
          }}
        >
          <Box
            component="img"
            src={previewUrl}
            alt="Drone preview"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not available%3C/text%3E%3C/svg%3E';
            }}
            sx={{
              maxWidth: '100%',
              maxHeight: 200,
              objectFit: 'contain',
              borderRadius: 1
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              disabled={disabled || uploading}
            >
              Change Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
            <IconButton
              size="small"
              color="error"
              onClick={handleRemoveImage}
              disabled={disabled || uploading}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            backgroundColor: '#fafafa',
            border: '2px dashed #e0e0e0'
          }}
        >
          <ImageIcon sx={{ fontSize: 48, color: '#9e9e9e' }} />
          <Typography variant="body2" color="text.secondary" align="center">
            Upload an image of your drone
            <br />
            (Max 10MB, JPG, PNG, etc.)
          </Typography>
          <Button
            component="label"
            variant="contained"
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            disabled={disabled || uploading}
          >
            {uploading ? 'Uploading...' : 'Select Image'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileSelect}
            />
          </Button>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Tip: A photo of your drone helps identify it quickly in the fleet
      </Typography>
    </Box>
  );
};

export default DroneImageUpload;
